import { memo, type ReactNode, useEffect } from 'react';

import { LuArrowDown01, LuArrowDownAZ, LuArrowDownUp, LuArrowUp01, LuArrowUpAZ } from 'react-icons/lu';

import { useDataTableContext } from './contexts/DataTableContext';
import { DATA_TABLE_SIZE_CLASSES } from './sizeClasses';
import type { CustomSortFunction } from './types';
import { SORT_TYPES } from './types';
import { cn } from '../../../utils/cn';

type SortType = 'alphabetical' | 'numerical';

type Props = {
    name: ReactNode;
    field: string;
    align?: 'left' | 'center';
    className?: string;
} & (
    | {
          sortable: true;
          sortType?: SortType;
          customSortFunction?: CustomSortFunction;
      }
    | { sortable?: false }
);

const getSortIcons = (sortType?: SortType) => {
    if (sortType === 'alphabetical') {
        return {
            asc: LuArrowDownAZ,
            desc: LuArrowUpAZ,
        };
    }
    if (sortType === 'numerical') {
        return {
            asc: LuArrowDown01,
            desc: LuArrowUp01,
        };
    }
    return {
        asc: LuArrowDownAZ,
        desc: LuArrowUpAZ,
    };
};

const DataTableColumnImpl: React.FC<Props> = (props) => {
    const { align = 'left', className, field, name, sortable } = props;
    const { setCustomSortFunction, setSortQuery, size, sortQuery } = useDataTableContext();
    const { header } = DATA_TABLE_SIZE_CLASSES[size];

    const sortType = sortable ? props.sortType : undefined;
    const { asc: AscIcon, desc: DescIcon } = getSortIcons(sortType);

    const customSortFunction = sortable ? props.customSortFunction : undefined;
    useEffect(() => {
        if (customSortFunction) setCustomSortFunction(field, customSortFunction);
    }, [field, customSortFunction, setCustomSortFunction]);

    const onClick = () => {
        const type = sortQuery.key === field ? sortQuery.type : SORT_TYPES.NO_SORTING;
        switch (type) {
            case SORT_TYPES.NO_SORTING:
                setSortQuery({ key: field, type: SORT_TYPES.DESC });
                break;
            case SORT_TYPES.DESC:
                setSortQuery({ key: field, type: SORT_TYPES.ASC });
                break;
            case SORT_TYPES.ASC:
                setSortQuery({ key: field, type: SORT_TYPES.NO_SORTING });
                break;
            default:
                throw new Error(`Unknown sort mode: ${type}`);
        }
    };

    const isUnsorted = sortQuery.type === SORT_TYPES.NO_SORTING || field !== sortQuery.key;
    const isDesc = sortQuery.type === SORT_TYPES.DESC && field === sortQuery.key;
    const isAsc = sortQuery.type === SORT_TYPES.ASC && field === sortQuery.key;

    return (
        <th
            role={'column-header-entry'}
            className={cn(
                header,
                align === 'center' ? 'text-center' : 'text-left',
                'border-b border-solid border-grey-outline-light select-none bg-white',
                sortable && 'cursor-pointer',
                className,
            )}
            onClick={async () => {
                if (sortable) await onClick();
            }}
        >
            <div
                className={`flex items-center gap-1 font-bold text-14 text-midnight${
                    align === 'center' ? ' justify-center' : ''
                }`}
            >
                <span>{name}</span>
                {sortable && (
                    <>
                        {isUnsorted && <LuArrowDownUp className="h-4 w-4" data-testid={'sort-icon-unsorted'} />}
                        {isDesc && <DescIcon className="h-4 w-4" data-testid={'sort-icon-desc'} />}
                        {isAsc && <AscIcon className="h-4 w-4" data-testid={'sort-icon-asc'} />}
                    </>
                )}
            </div>
        </th>
    );
};

export const DataTableColumn = memo(DataTableColumnImpl);
